import { useQuery } from '@apollo/client';
import { Button, Stack, Typography } from '@mui/material';
import {
  AdventureItinerariesToMoveDeparturesToDocument,
  Itinerary,
} from '@flashpack/graphql';
import { useOtherAdventureOptions } from '@src/adventure/common/useOtherAdventureOptions';
import { ConfirmationDialog } from '@src/design-system/dialogs/ConfirmationDialog';
import { AutocompleteField } from '@src/design-system/forms/autocomplete/AutocompleteField';
import { FC, useEffect, useMemo } from 'react';
import { Form, useForm, useFormState } from 'react-final-form';

export type TargetAdventure = { id: string; label: string };

type SelectedItinerary = Pick<Itinerary, 'id' | 'description'>;

type Option = { label: string; value: string };

type PickerValues = { adventureId?: string; itineraryId?: string };

interface PropTypes {
  open: boolean;
  currentAdventureId: string;
  onClose: () => void;
  onSelect: (itinerary: SelectedItinerary, adventure: TargetAdventure) => void;
}

const PickerFields: FC<{
  adventureOptions: Option[];
  onClose: () => void;
  onSelect: PropTypes['onSelect'];
}> = ({ adventureOptions, onClose, onSelect }) => {
  const form = useForm<PickerValues>();
  const {
    values: { adventureId, itineraryId },
  } = useFormState<PickerValues>();

  const { data, loading } = useQuery(AdventureItinerariesToMoveDeparturesToDocument, {
    skip: !adventureId,
    variables: { adventureId: adventureId ?? '' },
  });

  const itineraryOptions = useMemo<Option[]>(
    () =>
      (data?.adventure.itineraries ?? [])
        .filter(({ archived }) => !archived)
        .map(({ id, description }) => ({ value: id, label: description })),
    [data?.adventure.itineraries],
  );

  // A different adventure means the chosen itinerary no longer applies
  useEffect(() => {
    form.change('itineraryId', undefined);
  }, [adventureId, form]);

  const adventure = adventureOptions.find(({ value }) => value === adventureId);
  const itinerary = itineraryOptions.find(({ value }) => value === itineraryId);

  return (
    <Stack gap={2}>
      <AutocompleteField<Option, false, false, false>
        name="adventureId"
        label="Adventure"
        options={adventureOptions}
        getOptionValue={(option) => option.value}
        data-testid="move-departures-target-adventure"
      />
      <AutocompleteField<Option, false, false, false>
        name="itineraryId"
        label="Itinerary"
        options={itineraryOptions}
        loading={loading}
        readOnly={!adventureId}
        getOptionValue={(option) => option.value}
        data-testid="move-departures-target-itinerary"
      />
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!adventure || !itinerary}
          onClick={() =>
            adventure &&
            itinerary &&
            onSelect(
              { id: itinerary.value, description: itinerary.label },
              { id: adventure.value, label: adventure.label },
            )
          }
        >
          Continue
        </Button>
      </Stack>
    </Stack>
  );
};

/**
 * Picks an itinerary on a different adventure to move departures to. Moving departures across
 * adventures is Flashpack only; the server enforces this as well.
 */
export const MoveDeparturesToAdventureDialog: FC<PropTypes> = ({
  open,
  currentAdventureId,
  onClose,
  onSelect,
}) => {
  const { options: adventureOptions } = useOtherAdventureOptions(currentAdventureId, {
    skip: !open,
  });

  return (
    <ConfirmationDialog
      open={open}
      onCancel={onClose}
      title="Move departures to another adventure"
      hasCustomActions
      maxWidth="sm"
    >
      <Stack gap={2}>
        <Typography variant="bodyPara">
          Choose the adventure and itinerary to move the selected departures to.
        </Typography>
        {/* Remounts on open so the picker always starts empty */}
        {open && (
          <Form<PickerValues>
            onSubmit={() => undefined}
            render={() => (
              <PickerFields
                adventureOptions={adventureOptions}
                onClose={onClose}
                onSelect={onSelect}
              />
            )}
          />
        )}
      </Stack>
    </ConfirmationDialog>
  );
};
